package org.zq.astrock_ai.ui

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.togetherWith
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeContentPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.delay
import org.zq.astrock_ai.api.StockCodes
import org.zq.astrock_ai.data.MockStockData
import org.zq.astrock_ai.data.StockProfile
import org.zq.astrock_ai.data.WatchlistItem
import org.zq.astrock_ai.network.MarketCenterData
import org.zq.astrock_ai.network.MarketRepository
import org.zq.astrock_ai.theme.BinanceColors
import org.zq.astrock_ai.ui.components.AddWatchlistDialog
import org.zq.astrock_ai.ui.components.AiPredictionPanel
import org.zq.astrock_ai.ui.components.ChartSection
import org.zq.astrock_ai.ui.components.MetricCards
import org.zq.astrock_ai.ui.components.NewsPanel
import org.zq.astrock_ai.ui.components.RightSidebar
import org.zq.astrock_ai.ui.components.StockHeader
import org.zq.astrock_ai.ui.components.StockSelectorBar
import org.zq.astrock_ai.ui.components.TickerBar
import org.zq.astrock_ai.ui.components.MarketCenterPage
import org.zq.astrock_ai.ui.components.TopNavBar
import org.zq.astrock_ai.ui.components.WatchlistPage
import org.zq.astrock_ai.ui.components.WatchlistSidebar
import org.zq.astrock_ai.ui.research.StockResearchScreen

@Composable
fun StockPredictionScreen() {
    val repository = remember { MarketRepository() }
    val watchlistCodes = remember {
        mutableStateListOf<String>().apply { addAll(StockCodes.defaultWatchlist) }
    }
    var selectedIndex by remember { mutableIntStateOf(0) }
    var currentScreen by remember { mutableStateOf(AppScreen.Research) }
    var showAddWatchlistDialog by remember { mutableStateOf(false) }
    val scrollState = rememberScrollState()

    var indexQuotes by remember { mutableStateOf(MockStockData.indexQuotes) }
    var marketCenter by remember {
        mutableStateOf(
            MarketCenterData(
                overview = MockStockData.marketOverview,
                indices = MockStockData.indexQuotes,
                sectors = MockStockData.sectorQuotes,
                gainers = MockStockData.gainers,
                losers = MockStockData.losers,
                turnoverLeaders = MockStockData.turnoverLeaders,
            ),
        )
    }
    var stockProfiles by remember { mutableStateOf<Map<String, StockProfile>>(emptyMap()) }
    var klineCloses by remember { mutableStateOf<List<Float>?>(null) }
    var isLive by remember { mutableStateOf(false) }
    var addableStocks by remember { mutableStateOf(MockStockData.addableStocks(emptySet())) }

    LaunchedEffect(Unit) {
        while (true) {
            indexQuotes = repository.loadTicker()
            isLive = repository.isLive
            delay(30_000)
        }
    }

    LaunchedEffect(watchlistCodes.toList()) {
        val profiles = repository.loadWatchlist(watchlistCodes.toList())
        stockProfiles = profiles.associateBy { it.code }
        isLive = repository.isLive
        addableStocks = repository.loadAddableStocks(watchlistCodes.toSet())
    }

    LaunchedEffect(currentScreen) {
        if (currentScreen == AppScreen.MarketCenter) {
            marketCenter = repository.loadMarketCenter()
            isLive = repository.isLive
        }
    }

    val selectedCode = watchlistCodes.getOrElse(selectedIndex) { watchlistCodes.firstOrNull() }
    LaunchedEffect(selectedCode) {
        val code = selectedCode ?: return@LaunchedEffect
        klineCloses = repository.loadKlineCloses(code)
    }

    val watchlist by remember {
        derivedStateOf {
            watchlistCodes.mapNotNull { code ->
                stockProfiles[code]?.toWatchlistItem()
                    ?: MockStockData.profileByCode(code)?.toWatchlistItem()
            }
        }
    }
    val selectedStock = remember(watchlistCodes, selectedIndex, stockProfiles) {
        val code = watchlistCodes.getOrElse(selectedIndex) { watchlistCodes.firstOrNull() }
        code?.let { stockProfiles[it] ?: MockStockData.profileByCode(it) }
    }
    val chartSeed = remember(selectedStock?.code) { selectedStock?.code?.hashCode() ?: 0 }

    val selectStock: (Int) -> Unit = { index ->
        if (index in watchlist.indices) {
            selectedIndex = index
            currentScreen = AppScreen.StockDetail
        }
    }

    LaunchedEffect(selectedIndex) {
        if (currentScreen == AppScreen.StockDetail) {
            scrollState.animateScrollTo(0)
        }
    }

    val onSelectByCode: (String) -> Unit = { code ->
        if (code !in watchlistCodes) {
            watchlistCodes.add(code)
            selectedIndex = watchlistCodes.lastIndex
        } else {
            val index = watchlistCodes.indexOf(code)
            if (index >= 0) selectStock(index)
        }
        currentScreen = AppScreen.StockDetail
    }

    val openAddWatchlist: () -> Unit = { showAddWatchlistDialog = true }

    val addToWatchlist: (String) -> Unit = { code ->
        if (code !in watchlistCodes) {
            watchlistCodes.add(code)
            selectedIndex = watchlistCodes.lastIndex
        }
    }

    if (showAddWatchlistDialog) {
        AddWatchlistDialog(
            availableStocks = addableStocks,
            onDismiss = { showAddWatchlistDialog = false },
            onAdd = addToWatchlist,
        )
    }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .background(BinanceColors.Dark)
            .safeContentPadding(),
    ) {
        if (currentScreen == AppScreen.Research) {
            Box(
                Modifier
                    .fillMaxSize()
                    .background(Color(0xFFF7F9FC)),
            ) {
                StockResearchScreen()
            }
            return@Column
        }

        if (selectedStock == null) {
            Text(
                "暂无自选股，请添加股票",
                color = BinanceColors.Muted,
                modifier = Modifier.padding(24.dp),
            )
            return@Column
        }

        val stock = selectedStock

        BoxWithConstraints(Modifier.fillMaxSize()) {
            val windowSize = maxWidth.toWindowWidthSize()
            val isCompact = windowSize.isCompact
            val showLeftSidebar = maxWidth >= 1280.dp && currentScreen == AppScreen.StockDetail
            val showRightSidebar = maxWidth >= 1024.dp && currentScreen == AppScreen.StockDetail
            val contentPadding = if (isCompact) 12.dp else 16.dp
            val sectionSpacing = if (isCompact) 12.dp else 20.dp

            Column(Modifier.fillMaxSize()) {
                TopNavBar(
                    compact = isCompact,
                    currentScreen = currentScreen,
                    onScreenChange = { currentScreen = it },
                    isLive = isLive,
                )
                TickerBar(quotes = indexQuotes)

                AnimatedContent(
                    targetState = currentScreen,
                    transitionSpec = { fadeIn() togetherWith fadeOut() },
                    modifier = Modifier.fillMaxSize(),
                    label = "screenTransition",
                ) { screen ->
                    when (screen) {
                        AppScreen.MarketCenter -> {
                            MarketCenterPage(
                                onStockSelect = onSelectByCode,
                                compact = isCompact,
                                marketData = marketCenter,
                                modifier = Modifier
                                    .fillMaxSize()
                                    .verticalScroll(rememberScrollState())
                                    .padding(contentPadding),
                            )
                        }
                        AppScreen.Watchlist -> {
                            WatchlistPage(
                                items = watchlist,
                                selectedIndex = selectedIndex,
                                onSelect = selectStock,
                                onAddClick = openAddWatchlist,
                                modifier = Modifier
                                    .fillMaxSize()
                                    .verticalScroll(rememberScrollState())
                                    .padding(contentPadding),
                            )
                        }
                        AppScreen.StockDetail -> {
                            Column(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .verticalScroll(scrollState)
                                    .padding(horizontal = contentPadding, vertical = contentPadding),
                            ) {
                                if (showLeftSidebar) {
                                    Row(modifier = Modifier.fillMaxWidth()) {
                                        WatchlistSidebar(
                                            items = watchlist,
                                            selectedIndex = selectedIndex,
                                            onSelect = selectStock,
                                            onAddClick = openAddWatchlist,
                                        )
                                        Spacer(Modifier.width(12.dp))
                                        StockDetailContent(
                                            modifier = Modifier.weight(1f),
                                            watchlist = watchlist,
                                            selectedIndex = selectedIndex,
                                            selectedStock = stock,
                                            chartSeed = chartSeed,
                                            historyCloses = klineCloses,
                                            showStockSelector = false,
                                            showInlineSidebar = false,
                                            isCompact = isCompact,
                                            sectionSpacing = sectionSpacing,
                                            onSelectStock = selectStock,
                                            onSelectByCode = onSelectByCode,
                                        )
                                        Spacer(Modifier.width(12.dp))
                                        RightSidebar(
                                            stock = stock,
                                            onStockSelect = onSelectByCode,
                                            compact = false,
                                        )
                                    }
                                } else if (showRightSidebar) {
                                    Row(modifier = Modifier.fillMaxWidth()) {
                                        StockDetailContent(
                                            modifier = Modifier.weight(1f),
                                            watchlist = watchlist,
                                            selectedIndex = selectedIndex,
                                            selectedStock = stock,
                                            chartSeed = chartSeed,
                                            historyCloses = klineCloses,
                                            showStockSelector = true,
                                            showInlineSidebar = false,
                                            isCompact = isCompact,
                                            sectionSpacing = sectionSpacing,
                                            onSelectStock = selectStock,
                                            onSelectByCode = onSelectByCode,
                                        )
                                        Spacer(Modifier.width(12.dp))
                                        RightSidebar(
                                            stock = stock,
                                            onStockSelect = onSelectByCode,
                                            compact = false,
                                        )
                                    }
                                } else {
                                    StockDetailContent(
                                        modifier = Modifier.fillMaxWidth(),
                                        watchlist = watchlist,
                                        selectedIndex = selectedIndex,
                                        selectedStock = stock,
                                        chartSeed = chartSeed,
                                        historyCloses = klineCloses,
                                        showStockSelector = true,
                                        showInlineSidebar = true,
                                        isCompact = isCompact,
                                        sectionSpacing = sectionSpacing,
                                        onSelectStock = selectStock,
                                        onSelectByCode = onSelectByCode,
                                    )
                                }
                            }
                        }
                        AppScreen.Research -> {
                            // 由上方 early-return 全屏处理
                            Box(Modifier.fillMaxSize())
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun StockDetailContent(
    modifier: Modifier,
    watchlist: List<WatchlistItem>,
    selectedIndex: Int,
    selectedStock: StockProfile,
    chartSeed: Int,
    historyCloses: List<Float>?,
    showStockSelector: Boolean,
    showInlineSidebar: Boolean,
    isCompact: Boolean,
    sectionSpacing: androidx.compose.ui.unit.Dp,
    onSelectStock: (Int) -> Unit,
    onSelectByCode: (String) -> Unit,
) {
    Column(modifier = modifier) {
        if (showStockSelector) {
            StockSelectorBar(
                items = watchlist,
                selectedIndex = selectedIndex,
                onSelect = onSelectStock,
            )
            Spacer(Modifier.height(sectionSpacing))
        }
        StockHeader(stock = selectedStock, compact = isCompact)
        Spacer(Modifier.height(sectionSpacing))
        MetricCards(metrics = selectedStock.metrics, compact = isCompact)
        Spacer(Modifier.height(sectionSpacing))
        ChartSection(chartSeed = chartSeed, compact = isCompact, historyCloses = historyCloses)
        Spacer(Modifier.height(sectionSpacing))
        if (showInlineSidebar) {
            RightSidebar(
                stock = selectedStock,
                onStockSelect = onSelectByCode,
                compact = true,
            )
            Spacer(Modifier.height(sectionSpacing))
        }
        AiPredictionPanel(stock = selectedStock, compact = isCompact)
        Spacer(Modifier.height(sectionSpacing))
        NewsPanel(stock = selectedStock, compact = isCompact)
    }
}
